import { Box, type SxProps, type Theme } from "@mui/material";
import type { FC, ReactNode } from "react";
import { EVENT_LIST, ICON_TILE, NEUTRAL } from "../../../theme/tokens";

/**
 * `divided` — rows split by dashed hairlines: separate things that happened.
 * `timeline` — each row's tile hangs on a continuous rail to the next: one thing that led to another.
 */
export type EventListVariant = "divided" | "timeline";

type Props = {
	/** `EventListItem`s, in the order they happened. */
	children: ReactNode;
	variant: EventListVariant;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

const DIVIDED_SX: SxProps<Theme> = { "& > li + li": { borderTop: `1px dashed ${NEUTRAL[200]}` } };

/** The rail is drawn by the list, not the rows, so the last row simply has nothing to connect to. */
const TIMELINE_SX: SxProps<Theme> = (theme) => {
	const stepPad = theme.spacing(EVENT_LIST.STEP_PY);
	return {
		"& > li": { position: "relative", py: EVENT_LIST.STEP_PY },
		"& > li:not(:last-of-type)::after": {
			content: '""',
			position: "absolute",
			insetInlineStart: `${(ICON_TILE.XS.size - EVENT_LIST.CONNECTOR_PX) / 2}px`,
			top: `calc(${stepPad} + ${ICON_TILE.XS.size + EVENT_LIST.CONNECTOR_GAP_PX}px)`,
			bottom: `calc(${EVENT_LIST.CONNECTOR_GAP_PX}px - ${stepPad})`,
			width: `${EVENT_LIST.CONNECTOR_PX}px`,
			borderRadius: `${EVENT_LIST.CONNECTOR_PX}px`,
			backgroundColor: NEUTRAL[200],
		},
	};
};

/**
 * A short list of things that happened, each an `EventListItem`: split by dashed hairlines (`divided`), or hung on a
 * rail that connects each step's tile to the next (`timeline`) when the rows are one story told in order — the causes
 * that opened a request's slots, a record's history.
 */
export const EventList: FC<Props> = ({ children, variant, dataTestId, sx }) => (
	<Box
		component="ul"
		data-testid={dataTestId}
		data-variant={variant}
		sx={[{ listStyle: "none", m: 0, p: 0 }, variant === "timeline" ? TIMELINE_SX : DIVIDED_SX, ...(sx === undefined ? [] : Array.isArray(sx) ? sx : [sx])]}
	>
		{children}
	</Box>
);
